import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Alert, Button, EmptyState, Skeleton, TextLink, Temp_WideModal } from '@indipia/ui-core'
import { VendorFormPopup } from '@indipia/shared-features'
import {
  OrderApiError, type OrderScheduleResult, type OrderVendorRow, type ScheduleTaskOrderItem,
} from '../api/orderApi'
import {
  ORDER_MESSAGE, buildTaskRows, checkSendReady, draftFromRow, getSendAction, isOrderNotFoundCode, mapServerField,
  nextDirectTaskId, orderErrorMessage, type MaterialTaskRowModel, type OrderDraft,
} from '../constants/materialOrder.logic'
import { slotItem } from '../constants/signalView'
import {
  VENDOR_CANDIDATES_KEY, VENDOR_DIRECTORY_KEY, useMaterialOrderData, useOrderActions, useOrderDraft, useVendorDirectory,
  type BatchOutcome, type SendOutcome,
} from '../hooks/useMaterialOrders'
import { useTaskSignals } from '../hooks/useTaskSignals'
import { ErrorBanner } from '../components/AsyncState'
import { MaterialEvidenceModal } from '../components/material/MaterialEvidenceModal'
import { MaterialTaskList, type TaskSignalView } from '../components/material/MaterialTaskList'
import { EMPTY_SERVER_ERRORS, OrderComposePanel, type ServerFieldErrors } from '../components/material/OrderComposePanel'
import { OrderConflictModal } from '../components/material/OrderConflictModal'
import {
  OrderSendConfirmModal, type BatchBlockedEntry, type BatchConfirmEntry,
} from '../components/material/OrderSendConfirmModal'
import { VendorPicker } from '../components/material/VendorPicker'

// C-3 자재파트너배정·발주정보발송 팝업 — 간트·캘린더·리스트가 같은 팝업을 마운트한다(SigongPopup 과 같은 조건부 마운트 구조).
// 좌: 공종별 배정 목록(MaterialTaskList) / 우: 선택 공종의 발주정보 작성·회신·미리보기·발송(OrderComposePanel).
// 발송은 누르는 즉시 전송한다(지연·되돌리기 없음). 일괄 발송에만 직전 확인 창 1회.

/** 거래처·날짜가 바뀐 공종 1건 — 부모가 막대·마커 등을 국소 갱신한다(발송은 알리지 않음) */
export interface MaterialOrderChange {
  taskId: number
  vendorId: number | null
  vendorName: string | null
  orderDate: string | null
  deliveryDate: string | null
}

interface Props {
  siteId: number
  scheduleId: number
  siteName?: string
  /** 딥링크·신호 처리 버튼이 지정한 공종 — 열리면 그 공종 행을 선택·스크롤 */
  focusTaskId?: number | null
  onClose: () => void
  onChanged?: (change: MaterialOrderChange) => void
  /** 발주 설정(H-5) 화면으로 이동 — 없으면 [발주 설정으로] 링크를 그리지 않는다 */
  onOpenOrderSettings?: () => void
}

type NoticeKind = 'success' | 'warning' | 'error'
interface Notice { kind: NoticeKind; text: string }
interface BatchFailure { key: number; label: string; text: string }
interface BatchSummary { ok: number; failed: number; failures: BatchFailure[] }

const SAVE_FAILED_CODE = 'SAVE_FAILED'

function sendErrorText(err: OrderApiError, vendorName: string): string {
  if (err.code === SAVE_FAILED_CODE) return ORDER_MESSAGE.saveFailed
  return orderErrorMessage(err, { vendorName })
}

/** 서버 VALIDATION_ERROR(field) 를 입력칸에 붙인다. 칸을 못 찾으면 패널 상단 문구 */
function toServerFieldErrors(err: OrderApiError): ServerFieldErrors | null {
  if (err.code !== 'VALIDATION_ERROR') return null
  const target = mapServerField(err.field)
  const msg = ORDER_MESSAGE.panelCheckInput
  if (!target) return { items: {}, panel: msg }
  if (target.kind === 'item') return { items: { [target.index]: { [target.key]: msg } } }
  if (target.name === 'deliveryAddress') return { items: {}, deliveryAddress: msg }
  if (target.name === 'orderNote') return { items: {}, orderNote: msg }
  if (target.name === 'liftReqYn') return { items: {}, liftReqYn: msg }
  return { items: {}, panel: msg }
}

export function MaterialOrderPopup({ siteId, scheduleId, siteName, focusTaskId, onClose, onChanged, onOpenOrderSettings }: Props) {
  const qc = useQueryClient()
  const data = useMaterialOrderData(scheduleId)
  const signalsQ = useTaskSignals(siteId)
  const actions = useOrderActions({ scheduleId, siteId })
  const directory = useVendorDirectory()

  const models = useMemo(() => buildTaskRows(data.tasks, data.rows, data.settings), [data.tasks, data.rows, data.settings])

  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null)
  const [checked, setChecked] = useState<Set<number>>(() => new Set())
  const [notice, setNotice] = useState<Notice | null>(null)
  const [serverErrors, setServerErrors] = useState<ServerFieldErrors>(EMPTY_SERVER_ERRORS)
  const [freshRowId, setFreshRowId] = useState<number | null>(null)
  const [sending, setSending] = useState(false)
  const [batchOpen, setBatchOpen] = useState(false)
  const [batchSending, setBatchSending] = useState(false)
  const [batchSummary, setBatchSummary] = useState<BatchSummary | null>(null)
  const [conflict, setConflict] = useState<OrderVendorRow | null>(null)
  const [conflictBusy, setConflictBusy] = useState(false)
  const [evidence, setEvidence] = useState<{ taskId: number; row: OrderVendorRow } | null>(null)
  const [showVendorForm, setShowVendorForm] = useState(false)

  const selectedModel: MaterialTaskRowModel | null = models.find(m => m.taskId === selectedTaskId) ?? null
  const selectedRow: OrderVendorRow | null = selectedModel
    ? (selectedModel.vendorRows.find(r => r.orderVendorId === selectedRowId) ?? selectedModel.vendorRows[0] ?? null)
    : null

  const draftApi = useOrderDraft(selectedRow, {
    onSaved: actions.applyRow,
    onConflict: setConflict,
  })

  // 행의 입력 초안 — 선택 행은 입력 중인 값, 나머지는 서버 값(작성 상태 배지·일괄 발송 점검이 같이 씀)
  const draftOf = useCallback(
    (row: OrderVendorRow): OrderDraft => (selectedRow?.orderVendorId === row.orderVendorId ? draftApi.draft : draftFromRow(row)),
    [selectedRow, draftApi.draft],
  )

  // 안쪽 대화상자가 열려 있으면 바깥 팝업의 닫기(Esc)를 무시한다(Esc 가 둘 다 닫지 않게)
  const nestedOpenRef = useRef(false)
  nestedOpenRef.current = batchOpen || conflict != null || evidence != null || showVendorForm
  const closeAttemptedRef = useRef(false)
  // 저장 실패 후 한 번 막은 닫기 안전장치는 저장 성공·행 전환·입력 변경 시 다시 켠다(입력 유실 방지, 계속 실패해도 두 번째 닫기는 허용)
  const saveDone = draftApi.saveState === 'saved'
  useEffect(() => { closeAttemptedRef.current = false }, [draftApi.draft, selectedRow?.orderVendorId, saveDone]) // eslint-disable-line react-hooks/exhaustive-deps

  const headingRef = useRef<HTMLHeadingElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // ── 신호 ──
  const signalOf = useCallback((taskId: number): TaskSignalView => {
    if (signalsQ.isError) return { state: 'error' }
    if (!signalsQ.data) return { state: 'loading' }
    const item = slotItem(signalsQ.data.find(t => t.taskId === taskId), 'ORDER')
    return item ? { state: 'ok', item } : { state: 'error' }
  }, [signalsQ.isError, signalsQ.data])

  // ── 첫 진입 선택 ──
  const initializedRef = useRef(false)
  useEffect(() => {
    if (initializedRef.current || data.status !== 'ready') return
    if (signalsQ.isLoading) return   // 신호 로딩 중에는 첫 선택을 보류('빨강 우선' 규칙이 빠지지 않게)
    // 딥링크가 없고 신호 조회가 실패하면 첫 행으로 대체하지 않고 선택을 보류한다(오른쪽에 오류 안내, 다시 시도 성공 시 선택)
    if (focusTaskId == null && signalsQ.isError) return
    if (selectedTaskId != null) { initializedRef.current = true; return }   // 사용자가 먼저 고른 경우 덮어쓰지 않음
    const directRows = models.filter(m => m.directOrder)
    let target: MaterialTaskRowModel | undefined
    if (focusTaskId != null) {
      target = models.find(m => m.taskId === focusTaskId)
      if (!target) toast.error(ORDER_MESSAGE.taskNotFound)
    }
    if (!target) {
      const colorOf = (m: MaterialTaskRowModel) => {
        const v = signalOf(m.taskId)
        return v.state === 'ok' ? v.item.colorCode : null
      }
      target = directRows.find(m => colorOf(m) === 'RED') ?? directRows.find(m => colorOf(m) === 'YELLOW') ?? directRows[0]
    }
    initializedRef.current = true
    if (target) {
      setSelectedTaskId(target.taskId)
      setSelectedRowId(target.vendorRows[0]?.orderVendorId ?? null)
      const id = target.taskId
      requestAnimationFrame(() => {
        listRef.current?.querySelector<HTMLElement>(`[data-task-id="${id}"]`)?.scrollIntoView?.({ block: 'nearest' })
      })
    }
  }, [data.status, models, focusTaskId, signalsQ.isLoading, signalsQ.isError, selectedTaskId, signalOf])

  // 선택 행이 사라지면(소프트삭제 경합·교체) 같은 공종의 첫 행으로
  useEffect(() => {
    if (selectedModel && selectedRowId != null && !selectedModel.vendorRows.some(r => r.orderVendorId === selectedRowId)) {
      setSelectedRowId(selectedModel.vendorRows[0]?.orderVendorId ?? null)
    }
  }, [selectedModel, selectedRowId])

  // 체크한 행이 목록에서 사라지거나 일괄 대상이 아니게 되면 체크 해제
  const batchableIds = useMemo(() => {
    const s = new Set<number>()
    for (const m of models) for (const r of m.vendorRows) if (getSendAction(r).batch) s.add(r.orderVendorId)
    return s
  }, [models])
  useEffect(() => {
    setChecked(prev => {
      const next = new Set([...prev].filter(id => batchableIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [batchableIds])

  // 선택 행이 바뀌면 우측 패널 제목으로 포커스.
  // 단 왼쪽 목록의 거래처 선택칸을 눌러 선택된 경우는 포커스를 뺏지 않는다 — 뺏으면 드롭다운이 안 열려 한 번 더 눌러야 했다
  useEffect(() => {
    const active = document.activeElement
    if (active && listRef.current?.contains(active) && active.matches('input, [role="combobox"]')) return
    if (selectedRow) headingRef.current?.focus?.()
  }, [selectedRow?.orderVendorId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── 알림·오류 표시 ──
  const showError = useCallback((err: unknown, vendorName?: string) => {
    const e = err instanceof OrderApiError ? err : null
    setNotice({ kind: 'error', text: e ? sendErrorText(e, vendorName ?? '') : orderErrorMessage(err, vendorName ? { vendorName } : undefined) })
    if (e && isOrderNotFoundCode(e.code)) data.reload()
  }, [data])

  const notifyChanged = useCallback((c: MaterialOrderChange) => { onChanged?.(c) }, [onChanged])

  // ── 행 선택 ──
  const selectRow = useCallback(async (taskId: number, orderVendorId: number | null) => {
    if (taskId === selectedTaskId && orderVendorId === (selectedRow?.orderVendorId ?? null)) return
    if (selectedRow) {
      const ok = await draftApi.flush()
      if (!ok) { setNotice({ kind: 'error', text: ORDER_MESSAGE.saveFailed }); return }
    }
    setNotice(null)
    setBatchSummary(null)
    setServerErrors(EMPTY_SERVER_ERRORS)
    setSelectedTaskId(taskId)
    setSelectedRowId(orderVendorId)
  }, [selectedTaskId, selectedRow, draftApi])

  // ── 다음 직접발주 공종으로 이동([작성 완료·다음 공종]) — 현재 입력은 selectRow 가 먼저 저장한다 ──
  const nextTaskId = selectedModel ? nextDirectTaskId(models, selectedModel.taskId) : null
  const goNextTask = useCallback(() => {
    const next = nextTaskId != null ? models.find(m => m.taskId === nextTaskId) : undefined
    if (!next) return
    void selectRow(next.taskId, next.vendorRows[0]?.orderVendorId ?? null)
    requestAnimationFrame(() => {
      listRef.current?.querySelector<HTMLElement>(`[data-task-id="${next.taskId}"]`)?.scrollIntoView?.({ block: 'nearest' })
    })
  }, [nextTaskId, models, selectRow])

  // ── 거래처 지정·변경·해제 ──
  const pickVendor = useCallback(async (task: ScheduleTaskOrderItem, row: OrderVendorRow | null, vendorId: number) => {
    setNotice(null)
    try {
      if (row && selectedRow?.orderVendorId === row.orderVendorId) {
        const ok = await draftApi.flush()   // 이전 행의 입력을 새 행으로 복사하므로 먼저 저장
        if (!ok) { setNotice({ kind: 'error', text: ORDER_MESSAGE.saveFailed }); return }
      }
      let created: OrderVendorRow
      if (row) {
        const res = await actions.replace(row.orderVendorId, vendorId)
        actions.applyRow(res.replaced)
        actions.applyRow(res.created)
        created = res.created
      } else {
        created = await actions.assign(task.taskId, vendorId)
        actions.applyRow(created)
      }
      setServerErrors(EMPTY_SERVER_ERRORS)
      setFreshRowId(created.orderVendorId)
      setSelectedTaskId(task.taskId)
      setSelectedRowId(created.orderVendorId)
      notifyChanged({
        taskId: task.taskId, vendorId: created.vendorId, vendorName: created.vendorName,
        orderDate: created.orderDate, deliveryDate: created.deliveryDate,
      })
    } catch (e) {
      showError(e)
    }
  }, [selectedRow, draftApi, actions, notifyChanged, showError])

  const releaseVendor = useCallback(async (row: OrderVendorRow) => {
    setNotice(null)
    try {
      await actions.release(row.orderVendorId, row.taskId)
      setChecked(prev => { const n = new Set(prev); n.delete(row.orderVendorId); return n })
      const m = models.find(x => x.taskId === row.taskId)
      notifyChanged({ taskId: row.taskId, vendorId: null, vendorName: null, orderDate: m?.orderDate ?? null, deliveryDate: m?.deliveryDate ?? null })
    } catch (e) {
      showError(e)
    }
  }, [actions, models, notifyChanged, showError])

  // ── 날짜(C9) ──
  const changeDates = useCallback(async (
    taskId: number, patch: { orderDate?: string | null; deliveryDate?: string | null },
  ): Promise<OrderScheduleResult> => {
    const res = await actions.changeDates(taskId, patch)
    const m = models.find(x => x.taskId === taskId)
    const r = m?.vendorRows[0]
    notifyChanged({
      taskId, vendorId: r?.vendorId ?? m?.task.vendorId ?? null, vendorName: r?.vendorName ?? m?.task.vendorName ?? null,
      orderDate: res.orderDate, deliveryDate: res.deliveryDate,
    })
    return res
  }, [actions, models, notifyChanged])

  // ── 한 곳 발송 ──
  const handleOutcome = useCallback((out: SendOutcome, row: OrderVendorRow) => {
    if (out.result === 'SENT') {
      setNotice(null)
      setServerErrors(EMPTY_SERVER_ERRORS)
      setFreshRowId(prev => (prev === row.orderVendorId ? null : prev))
      return
    }
    if (out.result === 'UNKNOWN') {
      setNotice({ kind: 'warning', text: [out.partialMessage, ORDER_MESSAGE.sendResultUnknown].filter(Boolean).join(' · ') })
      return
    }
    const fieldErrors = toServerFieldErrors(out.error)
    if (fieldErrors) setServerErrors(fieldErrors)
    const base = sendErrorText(out.error, row.vendorName)
    setNotice({ kind: 'error', text: [out.partialMessage, base].filter(Boolean).join(' · ') })
    if (isOrderNotFoundCode(out.error.code)) data.reload()
  }, [data])

  const sendOne = useCallback(async (row: OrderVendorRow) => {
    if (sending) return
    setSending(true)
    setNotice(null)
    try {
      handleOutcome(await actions.send(row, { flush: draftApi.flush }), row)
    } catch (e) {
      showError(e, row.vendorName)
    } finally {
      setSending(false)
    }
  }, [sending, actions, draftApi.flush, handleOutcome, showError])

  const sendWithAvail = useCallback(async (row: OrderVendorRow) => {
    if (sending) return
    setSending(true)
    setNotice(null)
    try {
      handleOutcome(await actions.resendWithAvailDate(row, { flush: draftApi.flush }), row)
    } catch (e) {
      showError(e, row.vendorName)
    } finally {
      setSending(false)
    }
  }, [sending, actions, draftApi.flush, handleOutcome, showError])

  // ── 일괄 발송 ──
  const checkedRows = useMemo(() => {
    const out: Array<{ model: MaterialTaskRowModel; row: OrderVendorRow }> = []
    for (const m of models) for (const r of m.vendorRows) if (checked.has(r.orderVendorId)) out.push({ model: m, row: r })
    return out
  }, [models, checked])

  const batchPlan = useMemo(() => {
    const sendable: Array<BatchConfirmEntry & { row: OrderVendorRow }> = []
    const blocked: BatchBlockedEntry[] = []
    for (const { model, row } of checkedRows) {
      const check = checkSendReady(row, draftOf(row))
      const entry = { orderVendorId: row.orderVendorId, workTypeName: model.workTypeName, vendorName: row.vendorName, deliveryDate: row.deliveryDate }
      if (check.ok) sendable.push({ ...entry, row })
      else blocked.push({ ...entry, reasons: check.reasons })
    }
    return { sendable, blocked }
  }, [checkedRows, draftOf])

  const confirmBatch = useCallback(async () => {
    if (batchSending) return
    const targets = batchPlan.sendable
    setBatchSending(true)
    setNotice(null)
    try {
      const out: BatchOutcome = await actions.sendBatch(targets.map(t => t.row), { flush: draftApi.flush })
      const labelOf = new Map(targets.map(t => [t.orderVendorId, `${t.workTypeName} · ${t.vendorName}`] as const))
      const failures: BatchFailure[] = out.items.filter(i => i.status === 'FAILED').map(i => ({
        key: i.orderVendorId,
        label: labelOf.get(i.orderVendorId) ?? '',
        text: i.error ? sendErrorText(i.error, '') : ORDER_MESSAGE.error.UNKNOWN,
      }))
      setBatchSummary({ ok: out.succeeded, failed: out.failed, failures })
      // 성공 행은 체크 해제, 실패 행은 체크 유지
      const okIds = new Set(out.items.filter(i => i.status === 'SUCCESS').map(i => i.orderVendorId))
      setChecked(prev => new Set([...prev].filter(id => !okIds.has(id))))
      setBatchOpen(false)
    } catch (e) {
      setBatchOpen(false)
      showError(e)
    } finally {
      setBatchSending(false)
    }
  }, [batchSending, batchPlan.sendable, actions, draftApi.flush, showError])

  // ── 충돌 ──
  const takeLatest = useCallback(() => {
    draftApi.discard()
    setConflict(null)
  }, [draftApi])
  const keepMine = useCallback(async () => {
    if (!conflict) return
    setConflictBusy(true)
    try {
      await draftApi.overwrite(conflict)
      setConflict(null)
    } finally {
      setConflictBusy(false)
    }
  }, [conflict, draftApi])

  // ── 닫기 — 대기 중인 저장을 끝낸 뒤 닫는다(실패하면 한 번 알리고, 다시 닫으면 닫는다) ──
  const requestClose = useCallback(async () => {
    if (nestedOpenRef.current) return
    if (selectedRow && !closeAttemptedRef.current) {
      const ok = await draftApi.flush()
      if (!ok) {
        closeAttemptedRef.current = true
        setNotice({ kind: 'error', text: ORDER_MESSAGE.saveFailed })
        return
      }
    }
    onClose()
  }, [selectedRow, draftApi, onClose])

  const vendorPhone = selectedRow ? (directory.byId.get(selectedRow.vendorId)?.vendorPhone ?? null) : null
  const hasDirect = models.some(m => m.directOrder)

  return (
    <>
      {/* 표준 최대(xl 880)에선 품목 줄·공종명이 줄바꿈돼 임시로 넓힌 팝업(ui-core temp_WideModal, 1120)을 쓴다 */}
      <Temp_WideModal open onClose={() => { void requestClose() }} title={ORDER_MESSAGE.popupTitle}>
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-caption text-text-muted">{siteName}</span>
              <span className="text-caption text-text-muted">{ORDER_MESSAGE.popup.autoSaveHint}</span>
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={() => setShowVendorForm(true)}>{ORDER_MESSAGE.newVendor}</Button>
          </div>

          <div aria-live="polite" className="flex flex-col gap-2">
            {notice && <Alert kind={notice.kind} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</Alert>}
            {batchSummary && (
              <Alert kind={batchSummary.failed > 0 ? 'warning' : 'success'} role="status">
                {batchSummary.failed > 0
                  ? ORDER_MESSAGE.batchSummary(batchSummary.ok, batchSummary.failed)
                  : ORDER_MESSAGE.batchSentToast(batchSummary.ok, 0)}
                {batchSummary.failures.length > 0 && (
                  <ul className="mt-1 flex flex-col gap-1">
                    {batchSummary.failures.map(f => (
                      <li key={f.key} className="text-caption">{[f.label, f.text].filter(Boolean).join(' · ')}</li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}
          </div>

          {data.status === 'loading' && (
            <div aria-busy="true" aria-label={ORDER_MESSAGE.popup.loadingLabel} className="flex flex-col gap-2">
              {[0, 1, 2].map(i => <Skeleton key={i} height={56} />)}
            </div>
          )}
          {data.status === 'error' && <ErrorBanner message={ORDER_MESSAGE.loadFailed} onRetry={data.reload} />}

          {data.status === 'ready' && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* 좌(공종·거래처 목록)·우(발주 작성) 반반 — 넓힌 팝업 폭 안에서 목록의 공종명·거래처명이 잘리지 않게 목록 쪽을 넓혔다 */}
              <div ref={listRef} className="max-h-[60vh] min-w-0 overflow-y-auto overflow-x-hidden pr-1">
                <p className="mb-2 text-caption text-text-muted">{ORDER_MESSAGE.popup.listHint}</p>
                <MaterialTaskList
                  rows={models}
                  selectedTaskId={selectedTaskId}
                  selectedRowId={selectedRow?.orderVendorId ?? null}
                  checked={checked}
                  batchableIds={batchableIds}
                  signalOf={signalOf}
                  draftOf={draftOf}
                  onSelect={(t, r) => { void selectRow(t, r) }}
                  onToggleCheck={id => setChecked(prev => {
                    const n = new Set(prev)
                    if (n.has(id)) n.delete(id); else n.add(id)
                    return n
                  })}
                  onOpenEvidence={(taskId, row) => setEvidence({ taskId, row })}
                  onPickVendor={(task, row, vendorId) => { void pickVendor(task, row, vendorId) }}
                  onReleaseVendor={row => { void releaseVendor(row) }}
                />
              </div>

              <div className="max-h-[60vh] min-w-0 overflow-y-auto">
                {!hasDirect && (
                  <EmptyState
                    title={ORDER_MESSAGE.noDirectRows}
                    action={onOpenOrderSettings
                      ? <TextLink onClick={onOpenOrderSettings}>{ORDER_MESSAGE.goOrderSettings}</TextLink>
                      : undefined}
                  />
                )}
                {hasDirect && !selectedModel && (signalsQ.isError && focusTaskId == null && !initializedRef.current
                  ? <ErrorBanner message={ORDER_MESSAGE.signalError} onRetry={() => { void signalsQ.refetch() }} />
                  : <EmptyState title={ORDER_MESSAGE.popup.pickPrompt} />)}
                {hasDirect && selectedModel && !selectedModel.directOrder && (
                  <EmptyState title={ORDER_MESSAGE.notDirectCard} description={ORDER_MESSAGE.popup.pickNotDirect} />
                )}
                {selectedModel?.directOrder && !selectedRow && (
                  <div className="flex flex-col gap-2">
                    <h2 ref={headingRef} tabIndex={-1} className="text-body font-semibold text-text-primary">{selectedModel.workTypeName}</h2>
                    <p className="text-body-sm font-semibold text-text-primary">{ORDER_MESSAGE.popup.unassignedTitle}</p>
                    <p className="text-body-sm text-text-muted">{ORDER_MESSAGE.popup.unassignedHint}</p>
                    <VendorPicker
                      row={null}
                      taskId={selectedModel.taskId}
                      onPick={vendorId => { void pickVendor(selectedModel.task, null, vendorId) }}
                    />
                  </div>
                )}
                {selectedModel?.directOrder && selectedRow && (
                  <OrderComposePanel
                    model={selectedModel}
                    row={selectedRow}
                    signal={signalOf(selectedModel.taskId)}
                    vendorPhone={vendorPhone}
                    draft={draftApi.draft}
                    errors={draftApi.errors}
                    serverErrors={serverErrors}
                    saveState={draftApi.saveState}
                    saveError={draftApi.errorMessage}
                    freshVendor={freshRowId === selectedRow.orderVendorId}
                    sending={sending}
                    headingRef={headingRef}
                    onDraftChange={patch => { setServerErrors(EMPTY_SERVER_ERRORS); draftApi.setDraft(patch) }}
                    onRetrySave={() => { void draftApi.flush() }}
                    onSend={() => { void sendOne(selectedRow) }}
                    onResendWithAvailDate={() => { void sendWithAvail(selectedRow) }}
                    onPickVendor={vendorId => { void pickVendor(selectedModel.task, selectedRow, vendorId) }}
                    onDatesChange={patch => changeDates(selectedModel.taskId, patch)}
                    onNextTask={nextTaskId != null ? goNextTask : undefined}
                  />
                )}
              </div>
            </div>
          )}

          {data.status === 'ready' && hasDirect && (
            <div className="flex flex-col items-end gap-2 border-t border-border pt-3">
              {batchPlan.blocked.length > 0 && (
                <Alert kind="warning" role="status" className="self-stretch">{ORDER_MESSAGE.batchWritingWarn}</Alert>
              )}
              <Button
                type="button"
                variant="primary"
                disabled={checked.size === 0 || batchSending}
                onClick={() => { setBatchSummary(null); setBatchOpen(true) }}
              >
                {checked.size === 0 ? ORDER_MESSAGE.batchButtonEmpty : ORDER_MESSAGE.batchButton(checked.size)}
              </Button>
            </div>
          )}
        </div>
      </Temp_WideModal>

      <OrderSendConfirmModal
        open={batchOpen}
        sendable={batchPlan.sendable}
        blocked={batchPlan.blocked}
        sending={batchSending}
        onConfirm={() => { void confirmBatch() }}
        onCancel={() => setBatchOpen(false)}
      />
      <OrderConflictModal
        open={conflict != null}
        busy={conflictBusy}
        onTakeLatest={takeLatest}
        onKeepMine={() => { void keepMine() }}
        onClose={() => setConflict(null)}
      />
      {evidence && (
        <MaterialEvidenceModal
          open
          onClose={() => setEvidence(null)}
          taskId={evidence.taskId}
          row={evidence.row}
          vendorName={evidence.row.vendorName}
        />
      )}
      {showVendorForm && (
        <VendorFormPopup
          onClose={() => setShowVendorForm(false)}
          onSuccess={() => {
            setShowVendorForm(false)
            void qc.invalidateQueries({ queryKey: [VENDOR_DIRECTORY_KEY] })
            void qc.invalidateQueries({ queryKey: [VENDOR_CANDIDATES_KEY] })
          }}
        />
      )}
    </>
  )
}
